import { ButtonVariant, CoButton } from "components/CoButton";
import { RightSidebarDrawer } from "components/RightSidebar";
import SearchResultActions from "components/SearchResultActions";
import { detailPageUrl } from "helpers/pageUrl";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { useAppSelector } from "hooks/useStore";
import { File, Table2, Text } from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import { selectQuotesForPaper } from "store/slices/citedPapers";
import PaperAbstractTab from "./components/PaperAbstractTab";
import PaperPDFTab from "./components/PaperPDFTab";
import StudySnapshotTab from "./components/StudySnapshotTab";
import usePaperSidebarDrawer from "./hooks/usePaperSidebarDrawer";
import { Tabs, TabItem } from "design_system";

enum PaperSidebarDrawerTab {
  PDFTab = "PDF",
  AbstractTab = "Abstract",
  SnapshotTab = "Snapshot",
}

export default function PaperSidebarDrawer() {
  const {
    paper,
    quote,
    interactionId,
    isOpen,
    isActive,
    closePaperSidebarDrawer,
  } = usePaperSidebarDrawer();
  const [labels] = useLabels("features.paper-sidebar-drawer");
  const [initialPDFLoad, setInitialPDFLoad] = useState(false);

  // Get quotes for the current paper and interaction
  const quotes = useAppSelector((state) =>
    paper && interactionId
      ? selectQuotesForPaper(state, interactionId, paper.paper_id)
      : []
  );

  // Convert quote string to index
  const selectedQuoteIndex = useMemo(() => {
    if (!quote) return undefined;
    // If quote is a number string, convert to number
    if (!isNaN(Number(quote))) {
      return Number(quote);
    }
    // If it's a quote string, find the matching quote in the quotes array
    const matchingQuote = quotes.find((q) => q.quote === quote);
    return matchingQuote?.quoteIndex;
  }, [quote, quotes]);

  // Determine the initial tab based on whether the paper has a valid PDF
  const initialTab = paper?.has_valid_chat_pdf
    ? PaperSidebarDrawerTab.PDFTab
    : PaperSidebarDrawerTab.AbstractTab;

  const [selectedTab, setSelectedTab] =
    useState<PaperSidebarDrawerTab>(initialTab);

  // Update selected tab when paper changes
  useEffect(() => {
    if (paper) {
      setInitialPDFLoad(false);

      setSelectedTab(
        paper.has_valid_chat_pdf
          ? PaperSidebarDrawerTab.PDFTab
          : PaperSidebarDrawerTab.AbstractTab
      );
    }
  }, [paper]);

  const handleLoadError = () => {
    if (!initialPDFLoad) {
      setSelectedTab(PaperSidebarDrawerTab.AbstractTab);
      setInitialPDFLoad(true);
    }
  };

  const detailsPageUrl = paper
    ? detailPageUrl(paper?.url_slug, paper?.paper_id)
    : "#";

  const tabs: {
    id: PaperSidebarDrawerTab;
    icon: React.ReactElement;
    label: string;
  }[] = [
    ...(paper?.has_valid_chat_pdf
      ? [
          {
            id: PaperSidebarDrawerTab.PDFTab,
            icon: <File />,
            label: labels["pdf"],
          },
        ]
      : []),
    {
      id: PaperSidebarDrawerTab.AbstractTab,
      icon: <Text />,
      label: labels["abstract"],
    },
    {
      id: PaperSidebarDrawerTab.SnapshotTab,
      icon: <Table2 />,
      label: labels["snapshot"],
    },
  ];

  const headerContent = {
    title: labels["paper"],
    actionBar: paper && (
      <Tabs aria-label="Paper Tab" className="mt-2 mb-[-1px] px-4">
        {tabs.map((tab) => (
          <TabItem
            isActive={selectedTab === tab.id}
            key={tab.id}
            icon={tab.icon}
            onClick={() => setSelectedTab(tab.id)}
          >
            {tab.label}
          </TabItem>
        ))}
      </Tabs>
    ),
  };

  const footerContent = paper && (
    <div className="flex justify-between w-full">
      <CoButton
        href={detailsPageUrl}
        target="_blank"
        icon="icon-info"
        label={labels["details"]}
        size="sm"
        variant={ButtonVariant.Secondary}
      />
      <SearchResultActions
        item={paper}
        searchPage={0} //TODO(Jimmy): Add page number
        paperIndex={0} //TODO(Jimmy): Add index number
        showSaveBtn
        showCiteBtn
        showShareBtn
        mixpanelActionSource={MixpanelActionSource.PaperSidebarDrawer}
      />
    </div>
  );

  // Render the appropriate content based on selected tab
  const renderTabContent = () => {
    if (!paper) return null;

    switch (selectedTab) {
      case PaperSidebarDrawerTab.PDFTab:
        return (
          <PaperPDFTab
            paperId={paper.paper_id}
            title={paper.title}
            selectedQuoteIndex={selectedQuoteIndex}
            quotes={quotes}
            onLoadError={handleLoadError}
          />
        );
      case PaperSidebarDrawerTab.AbstractTab:
        return (
          <PaperAbstractTab
            paper={paper}
            selectedQuoteIndex={selectedQuoteIndex}
            quotes={quotes}
          />
        );
      case PaperSidebarDrawerTab.SnapshotTab:
        return <StudySnapshotTab paper={paper} />;
      default:
        return null;
    }
  };

  return (
    <RightSidebarDrawer
      resizable
      isOpen={isOpen}
      isActive={isActive}
      onClose={closePaperSidebarDrawer}
      fullHeight
      headerContent={headerContent}
      footer={footerContent}
      size="lg"
      minWidth="lg"
    >
      <div className="max-w-full h-full">{renderTabContent()}</div>
    </RightSidebarDrawer>
  );
}
